/**
 * useSimpleInputValidation - Shared Hook for Simple Input Components
 *
 * Provides common validation and touched state management for simple input components
 * that primarily transport values (no complex calculations or value map building).
 *
 * Pattern Benefits:
 * - DRY: Shared validation/touched logic across simple input components
 * - Consistency: Same behavior across all simple input components
 * - Testability: Validation logic can be tested independently
 * - Maintainability: Single source of truth for validation patterns
 *
 * Use this for components like:
 * - MillageInfoInputs (validates millage rates/durations)
 * - DRFInfoInputs (validates balance constraints)
 * - Other simple form components with validation
 *
 * For complex components with calculations/value maps, use the full pattern:
 * @see docs/guides/input-component-pattern.md
 *
 * @example
 * const {
 *   handlers: { handleInputChange, markTouched },
 *   computed: { validationErrors, allFieldsTouched }
 * } = useSimpleInputValidation({
 *   values,
 *   onUpdate,
 *   onBlur,
 *   showValidation,
 *   requiredFields: ["field1", "field2"],
 *   validate: (engineValues) => Builder.validateInputs(engineValues),
 *   convertToEngine: (formValues) => ({ ... }),
 *   mapErrorsToFields: (error) => {
 *     if (error.includes("Field1")) return "field1";
 *     return null;
 *   }
 * });
 */

import { useCallback, useMemo, useState } from 'react';

interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

interface UseSimpleInputValidationOptions<TForm, TEngine> {
  /** Form values (string format from UI) */
  values: TForm;
  /** Callback when values change */
  onUpdate: (values: TForm) => void;
  /** Optional callback when any field is blurred */
  onBlur?: () => void;
  /** Whether to show all validation errors (e.g., user clicked Continue) */
  showValidation?: boolean;
  /** List of required field names */
  requiredFields: string[];
  /** Function to convert form values to engine format for validation */
  convertToEngine: (formValues: TForm) => TEngine | null;
  /** Function to validate engine values (returns errors array) */
  validate: (engineValues: TEngine) => ValidationResult;
  /** Function to map error messages to field names (returns field name or null) */
  mapErrorsToFields: (error: string) => string | null;
  /** Optional function to check if an error should be shown based on touched fields (for relationship errors) */
  shouldShowError?: (error: string, touched: Set<string>) => boolean;
}

export const useSimpleInputValidation = <TForm extends Record<string, string>, TEngine>({
  values,
  onUpdate,
  onBlur,
  showValidation = false,
  requiredFields,
  convertToEngine,
  validate,
  mapErrorsToFields,
  shouldShowError,
}: UseSimpleInputValidationOptions<TForm, TEngine>) => {
  // State
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // Handlers
  const markTouched = useCallback(
    (field: string) => {
      setTouched((prev) => new Set(prev).add(field));
      // Call parent onBlur handler when any field is blurred
      onBlur?.();
    },
    [onBlur]
  );

  const handleInputChange = useCallback(
    (field: keyof TForm, value: string) => {
      onUpdate({
        ...values,
        [field]: value,
      });
    },
    [values, onUpdate]
  );

  // Computed values
  // Check if all fields have been touched
  const allFieldsTouched = useMemo(() => {
    return requiredFields.every((field) => touched.has(field));
  }, [touched, requiredFields]);

  // Validation errors using builder validation (single source of truth)
  const validationErrors = useMemo(() => {
    const engineInputs = convertToEngine(values);

    // If fields are empty or invalid, don't validate yet (wait for user input)
    if (!engineInputs) {
      return [];
    }

    // Use provided validation function for consistency
    const validation = validate(engineInputs);
    const allErrors = validation.errors;

    // Only show validation errors if:
    // 1. showValidation is true (user clicked Continue), OR
    // 2. All fields in the section have been touched
    if (!showValidation && !allFieldsTouched) {
      return [];
    }

    // If showValidation is true, show all errors regardless of touched state
    if (showValidation) {
      return allErrors;
    }

    // Filter errors based on touched fields (only reached if allFieldsTouched is true)
    const filteredErrors: string[] = [];

    for (const error of allErrors) {
      // Use custom shouldShowError function if provided (for relationship errors)
      if (shouldShowError) {
        const customResult = shouldShowError(error, touched);
        if (customResult === true) {
          filteredErrors.push(error);
          continue;
        }
        // If false, fall through to standard field mapping
      }

      // Use standard field mapping
      const fieldName = mapErrorsToFields(error);
      if (fieldName && touched.has(fieldName)) {
        filteredErrors.push(error);
      } else if (!fieldName) {
        // Unknown error - show if any field is touched
        const hasTouchedField = Array.from(touched).length > 0;
        if (hasTouchedField) {
          filteredErrors.push(error);
        }
      }
    }

    return filteredErrors;
  }, [
    values,
    touched,
    showValidation,
    allFieldsTouched,
    convertToEngine,
    validate,
    mapErrorsToFields,
    shouldShowError,
  ]);

  return {
    // Handlers
    handlers: {
      handleInputChange,
      markTouched,
    },

    // Computed values
    computed: {
      validationErrors,
      allFieldsTouched,
    },
  };
};
